// src/pages/GroupDetailPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, Share2, Mail, Settings,
  RefreshCw, UserPlus, LogOut, Trash2, ClipboardList,
  Globe, Lock, EyeOff, Check, X, AlertTriangle, Layers,
  FlaskConical, BarChart2, Image  // Add these
} from 'lucide-react';
import { groupsAPI, invitationsAPI } from '../api/groupsApi';
import { useAuth } from '../contexts/AuthContext';
import MemberRow from '../components/Groups/MemberRow';
import SharedResourceCard from '../components/Groups/SharedResourceCard';
import InvitationRow from '../components/Groups/InvitationRow';
import InviteUsersModal from '../components/Groups/InviteUsersModal';
import GroupSettingsPanel from '../components/Groups/GroupSettingsPanel';
import BulkShareModal from '../components/Groups/BulkShareModal';
import './GroupDetailPage.css';

const TABS = [
  { key: 'members',          label: 'Members',         icon: Users,         adminOnly: false },
  { key: 'resources',        label: 'Shared Resources', icon: Share2,        adminOnly: false },
  { key: 'invitations',      label: 'Invitations',      icon: Mail,          adminOnly: true  },
  { key: 'join_requests',    label: 'Join Requests',    icon: ClipboardList, adminOnly: true  },
  { key: 'settings',         label: 'Settings',         icon: Settings,      adminOnly: true  },
];

const GroupDetailPage = () => {
  const { groupId } = useParams();
  const navigate    = useNavigate();
  const { user: currentUser } = useAuth();

  // ── Data ──────────────────────────────────────────────────────────────────
  const [group,           setGroup]           = useState(null);
  const [members,         setMembers]         = useState([]);
  const [sharedResources, setSharedResources] = useState([]);
  const [invitations,     setInvitations]     = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [showBulkShareModal, setShowBulkShareModal] = useState(false);
  const [loading,         setLoading]         = useState(true);
  const [activeTab,       setActiveTab]       = useState('members');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [actionLoading,   setActionLoading]   = useState(null);
  const [error,           setError]           = useState('');
  const [success,         setSuccess]         = useState('');

  // ── Derived ───────────────────────────────────────────────────────────────
  const userRole  = group?.user_role;
  const isAdmin   = userRole === 'owner' || userRole === 'admin';
  const isOwner   = userRole === 'owner';
  const canInvite = isAdmin || (group?.allow_member_invite && userRole === 'editor');

  const [autoShareStatus, setAutoShareStatus] = useState(null);
  const [showAutoShareSettings, setShowAutoShareSettings] = useState(false);

  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin);

  const fetchAutoShareStatus = useCallback(async () => {
    try {
      const data = await groupsAPI.getAutoShareStatus(groupId);
      setAutoShareStatus(data);
    } catch (e) {
      console.error('Failed to fetch auto-share status:', e);
    }
  }, [groupId]);

  // ── Fetchers ──────────────────────────────────────────────────────────────
  const fetchGroup = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await groupsAPI.getGroup(groupId);
      setGroup(data);
    } catch {
      setError('Failed to load group.');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  const fetchMembers = useCallback(async () => {
    try {
      const data = await groupsAPI.getMembers(groupId);
      setMembers(Array.isArray(data) ? data : (data.results ?? []));
    } catch (e) { console.error(e); }
  }, [groupId]);

  const fetchSharedResources = useCallback(async () => {
    try {
      const data = await groupsAPI.getSharedResources(groupId);
      setSharedResources(Array.isArray(data) ? data : (data.results ?? []));
    } catch (e) { console.error(e); }
  }, [groupId]);

  const fetchInvitations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await groupsAPI.getInvitations(groupId);
      setInvitations(Array.isArray(data) ? data : (data.results ?? []));
    } catch (e) { console.error(e); }
  }, [groupId, isAdmin]);

  const fetchPendingRequests = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await groupsAPI.getPendingRequests(groupId);
      setPendingRequests(Array.isArray(data) ? data : (data.results ?? []));
    } catch (e) { console.error(e); }
  }, [groupId, isAdmin]);

  useEffect(() => { fetchGroup(); }, [fetchGroup]);

  useEffect(() => {
    if (!group) return;
    fetchMembers();
    fetchSharedResources();
    fetchAutoShareStatus(); // Add this
    if (isAdmin) {
      fetchInvitations();
      fetchPendingRequests();
    }
  }, [group, fetchMembers, fetchSharedResources, fetchAutoShareStatus, fetchInvitations, fetchPendingRequests, isAdmin]);

  // ── Auto-clear success/error ──────────────────────────────────────────────
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3500);
    return () => clearTimeout(t);
  }, [success]);

  // ── Member actions ────────────────────────────────────────────────────────
  const handleChangeRole = async (userId, newRole) => {
    setActionLoading(`role-${userId}`);
    try {
      await groupsAPI.changeRole(groupId, userId, newRole);
      setMembers((prev) =>
        prev.map((m) =>
          String(m.user_details?.id) === String(userId) ? { ...m, role: newRole } : m
        )
      );
      setSuccess('Role updated.');
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.role?.[0] || 'Failed to change role.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the group?')) return;
    setActionLoading(`remove-${userId}`);
    try {
      await groupsAPI.removeMember(groupId, userId);
      setMembers((prev) =>
        prev.filter((m) => String(m.user_details?.id) !== String(userId))
      );
      setSuccess('Member removed.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to remove member.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Resource actions ──────────────────────────────────────────────────────
  // Add this function inside GroupDetailPage component

  const getResourceTypeStats = () => {
    const typeCount = {};
    sharedResources.forEach(r => {
      const type = r.resource_type || 'unknown';
      typeCount[type] = (typeCount[type] || 0) + 1;
    });

    const typeIcons = {
      experiment: <FlaskConical size={12} />,
      photo_job: <Image size={12} />,
      analysis: <BarChart2 size={12} />,
    };

    return Object.entries(typeCount).map(([type, count]) => (
      <span key={type} className="gdp__resources-type-badge">
        {typeIcons[type] || <Share2 size={12} />}
        {type}: {count}
      </span>
    ));
  };
  const handleUnshare = async (sharedResourceId) => {
    if (!window.confirm('Remove this resource from the group?')) return;
    try {
      await groupsAPI.unshareResource(sharedResourceId);
      setSharedResources((prev) => prev.filter((r) => r.id !== sharedResourceId));
      setSuccess('Resource removed.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to unshare resource.');
    }
  };

  const handleChangePermission = async (sharedResourceId, newLevel) => {
    try {
      await groupsAPI.changeResourcePermission(sharedResourceId, newLevel);
      setSharedResources((prev) =>
        prev.map((r) =>
          r.id === sharedResourceId ? { ...r, permission_level: newLevel } : r
        )
      );
      setSuccess('Permission updated.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to change permission.');
    }
  };

  // ── Invitation actions ────────────────────────────────────────────────────
  const handleCancelInvitation = async (invId) => {
    try {
      await invitationsAPI.cancel(invId);
      setInvitations((prev) => prev.filter((i) => i.id !== invId));
      setSuccess('Invitation cancelled.');
    } catch {
      setError('Failed to cancel invitation.');
    }
  };

  const handleResendInvitation = async (invId) => {
    try {
      await invitationsAPI.resend(invId);
      setSuccess('Invitation resent.');
    } catch {
      setError('Failed to resend invitation.');
    }
  };

  // ── Join request actions ──────────────────────────────────────────────────
  const handleApproveJoin = async (userId) => {
    setActionLoading(`approve-${userId}`);
    try {
      await groupsAPI.approveJoin(groupId, userId);
      setPendingRequests((prev) =>
        prev.filter((m) => String(m.user_details?.id) !== String(userId))
      );
      fetchMembers();
      setSuccess('Join request approved.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to approve request.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeclineJoin = async (userId) => {
    setActionLoading(`decline-${userId}`);
    try {
      await groupsAPI.declineJoin(groupId, userId);
      setPendingRequests((prev) =>
        prev.filter((m) => String(m.user_details?.id) !== String(userId))
      );
      setSuccess('Join request declined.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to decline request.');
    } finally {
      setActionLoading(null);
    }
  };

  // ── Leave / Delete ────────────────────────────────────────────────────────
  const handleLeave = async () => {
    if (!window.confirm('Leave this group?')) return;
    try {
      await groupsAPI.leaveGroup(groupId);
      navigate('/groups');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to leave group.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete "${group.name}"? This cannot be undone.`)) return;
    try {
      await groupsAPI.deleteGroup(groupId);
      navigate('/groups');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete group.');
    }
  };

  // ── Settings saved ────────────────────────────────────────────────────────
  const handleSettingsSaved = (updatedGroup) => {
    setGroup(updatedGroup);
    setSuccess('Group settings saved.');
  };

  const handleBulkShareComplete = (result) => {
    fetchSharedResources();
    fetchAutoShareStatus(); // Refresh auto-share status
    setSuccess('Resources shared successfully!');
  };

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="gdp gdp--loading">
        <RefreshCw size={32} className="spinning" />
        <span>Loading group…</span>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="gdp gdp--error">
        <AlertTriangle size={40} />
        <p>{error || 'Group not found.'}</p>
        <button className="btn btn--ghost" onClick={() => navigate('/groups')}>
          <ArrowLeft size={15} /> Back to Groups
        </button>
      </div>
    );
  }

  return (
    <div className="gdp">

      {/* ── Back breadcrumb ── */}
      <div className="gdp__breadcrumb">
        <button className="btn btn--ghost btn--sm" onClick={() => navigate('/groups')}>
          <ArrowLeft size={15} /> All Groups
        </button>
      </div>

      {/* ── Hero banner ── */}
      <div className="gdp__hero">
        <div className="gdp__hero-avatar">
          {group.avatar
            ? <img src={group.avatar} alt={group.name} />
            : <div className="gdp__hero-avatar-placeholder">{group.name.charAt(0).toUpperCase()}</div>
          }
        </div>

        <div className="gdp__hero-body">
          <h1 className="gdp__hero-name">{group.name}</h1>
          {group.description && (
            <p className="gdp__hero-desc">{group.description}</p>
          )}
          <div className="gdp__hero-meta">
            <VisibilityBadge visibility={group.visibility} />
            <span className="gdp__meta-chip">
              <Users size={13} /> {group.members_count} member{group.members_count !== 1 ? 's' : ''}
            </span>
            {userRole && (
              <span className={`gdp__role-badge gdp__role-badge--${userRole}`}>
                {userRole}
              </span>
            )}
            {group.visibility === 'public' && (
              <span className="gdp__meta-chip gdp__meta-chip--muted">
                Default role: <strong>{group.default_member_role}</strong>
              </span>
            )}
          </div>
        </div>

        <div className="gdp__hero-actions">
          {canInvite && (
            <button
              className="btn btn--primary btn--sm"
              onClick={() => setShowInviteModal(true)}
            >
              <UserPlus size={14} /> Invite
            </button>
          )}
          {userRole && !isOwner && (
            <button className="btn btn--ghost btn--sm btn--danger" onClick={handleLeave}>
              <LogOut size={14} /> Leave
            </button>
          )}
          {isOwner && (
            <button className="btn btn--ghost btn--sm btn--danger" onClick={handleDelete}>
              <Trash2 size={14} /> Delete Group
            </button>
          )}
        </div>
      </div>

      {/* ── Toast messages ── */}
      {error && (
        <div className="gdp__toast gdp__toast--error">
          <AlertTriangle size={15} />
          <span>{error}</span>
          <button onClick={() => setError('')}><X size={14} /></button>
        </div>
      )}
      {success && (
        <div className="gdp__toast gdp__toast--success">
          <Check size={15} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')}><X size={14} /></button>
        </div>
      )}

      {/* ── Tab bar ── */}
      <div className="gdp__tabs" role="tablist">
        {visibleTabs.map((tab) => {
          const Icon  = tab.icon;
          const count = tabCount(tab.key, members, sharedResources, invitations, pendingRequests);
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`gdp__tab ${activeTab === tab.key ? 'gdp__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <Icon size={14} />
              {tab.label}
              {count > 0 && <span className="gdp__tab-badge">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* ── Tab panels ── */}
      <div className="gdp__panel">

        {/* Members */}
        {activeTab === 'members' && (
          <div className="gdp__members">
            {members.length === 0 ? (
              <EmptyState icon={<Users size={36} />} message="No members yet." />
            ) : (
              members.map((m) => (
                <MemberRow
                  key={m.id}
                  membership={m}
                  currentUserRole={userRole}
                  currentUserId={currentUser?.id}
                  actionLoading={actionLoading}
                  onChangeRole={handleChangeRole}
                  onRemove={handleRemoveMember}
                  groupDefaultRole={group.default_member_role}
                  groupVisibility={group.visibility}
                />
              ))
            )}
          </div>
        )}

        {/* Shared Resources */}
        {activeTab === 'resources' && (
            <div className="gdp__resources">
              <div className="gdp__section-header">
                <h3>Shared Resources</h3>
                <div className="d-flex gap-2">
                  {/* Show auto-share status if enabled */}
                  {autoShareStatus?.auto_share_enabled && (
                      <div className="d-flex align-items-center me-2">
                        <span className="badge bg-info me-1" title="Auto-sharing enabled">
                          <Settings size={12} className="me-1"/>
                          Auto-share: {autoShareStatus.permission_level}
                        </span>
                      </div>
                  )}
                  <button
                      className="btn btn--primary btn--sm"
                      onClick={() => setShowBulkShareModal(true)}
                  >
                    <Layers size={14} className="me-1"/>
                    Bulk Share
                  </button>
                </div>
              </div>

              {sharedResources.length === 0 ? (
                  <EmptyState
                      icon={<Share2 size={36}/>}
                      message="No resources shared with this group yet."
                      hint={"Use the 'Bulk Share' button above to share your resources. Resources shared with this group will appear here"}
                  />
              ) : (
                  <>
                    {/* Optional: Add a summary bar showing share statistics */}
                    <div className="gdp__resources-summary">
                  <span className="gdp__resources-count">
                    <Share2
                        size={14}/> {sharedResources.length} shared resource{sharedResources.length !== 1 ? 's' : ''}
                  </span>
                      <span className="gdp__resources-types">
                    {getResourceTypeStats()}
                  </span>
                    </div>

                    <div className="gdp__resources-list">
                      {sharedResources.map((r) => (
                          <SharedResourceCard
                              key={r.id}
                              resource={r}
                              canManage={isAdmin || String(r.shared_by) === String(currentUser?.id)}
                              onUnshare={handleUnshare}
                              onChangePermission={handleChangePermission}
                          />
                      ))}
                    </div>
                  </>
              )}
            </div>
        )}

        {/* Invitations (admin) */}
        {activeTab === 'invitations' && isAdmin && (
            <div className="gdp__invitations">
              <div className="gdp__section-header">
                <h3>Pending Invitations</h3>
                <button
                    className="btn btn--primary btn--sm"
                    onClick={() => setShowInviteModal(true)}
                >
                  <UserPlus size={14}/> Send Invitations
                </button>
              </div>
              {invitations.length === 0 ? (
                  <EmptyState icon={<Mail size={36}/>} message="No pending invitations."/>
              ) : (
                  invitations.map((inv) => (
                      <InvitationRow
                          key={inv.id}
                          invitation={inv}
                          onCancel={handleCancelInvitation}
                  onResend={handleResendInvitation}
                />
              ))
            )}
          </div>
        )}

        {/* Join Requests (admin) */}
        {activeTab === 'join_requests' && isAdmin && (
          <div className="gdp__join-requests">
            <div className="gdp__section-header">
              <h3>Pending Join Requests</h3>
              <span className="gdp__section-hint">
                Users who requested to join this public group
              </span>
            </div>
            {pendingRequests.length === 0 ? (
              <EmptyState
                icon={<ClipboardList size={36} />}
                message="No pending join requests."
              />
            ) : (
              pendingRequests.map((m) => (
                <JoinRequestRow
                  key={m.id}
                  membership={m}
                  actionLoading={actionLoading}
                  onApprove={handleApproveJoin}
                  onDecline={handleDeclineJoin}
                />
              ))
            )}
          </div>
        )}

        {/* Settings (admin) */}
        {activeTab === 'settings' && isAdmin && (
          <GroupSettingsPanel
            group={group}
            isOwner={isOwner}
            onSaved={handleSettingsSaved}
            onDeleted={() => navigate('/groups')}
            onError={setError}
          />
        )}
      </div>

      {/* ── Invite modal ── */}
      {showInviteModal && (
        <InviteUsersModal
          groupId={groupId}
          group={group}
          existingMemberIds={members.map((m) => m.user_details?.id)}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            fetchInvitations();
            setSuccess('Invitations sent.');
          }}
        />
      )}
      {showBulkShareModal && (
        <BulkShareModal
          show={showBulkShareModal}
          onHide={() => setShowBulkShareModal(false)}
          group={group}
          onShared={handleBulkShareComplete}
        />
      )}
    </div>
  );
};

// ── Small helpers ─────────────────────────────────────────────────────────────

const tabCount = (key, members, resources, invitations, pending) => {
  if (key === 'members')       return members.length;
  if (key === 'resources')     return resources.length;
  if (key === 'invitations')   return invitations.length;
  if (key === 'join_requests') return pending.length;
  return 0;
};

const VisibilityBadge = ({ visibility }) => {
  const map = {
    public:  { icon: <Globe  size={12} />, label: 'Public'  },
    private: { icon: <Lock   size={12} />, label: 'Private' },
    secret:  { icon: <EyeOff size={12} />, label: 'Secret'  },
  };
  const { icon, label } = map[visibility] ?? map.private;
  return (
    <span className={`gdp__vis-badge gdp__vis-badge--${visibility}`}>
      {icon} {label}
    </span>
  );
};

const EmptyState = ({ icon, message, hint }) => (
  <div className="gdp__empty">
    <div className="gdp__empty-icon">{icon}</div>
    <p className="gdp__empty-msg">{message}</p>
    {hint && <p className="gdp__empty-hint">{hint}</p>}
  </div>
);

const JoinRequestRow = ({ membership, actionLoading, onApprove, onDecline }) => {
  const userId = membership.user_details?.id;
  const approving = actionLoading === `approve-${userId}`;
  const declining = actionLoading === `decline-${userId}`;

  return (
    <div className="gdp__jr-row">
      <div className="gdp__jr-avatar">
        {membership.user_details?.avatar_url
          ? <img src={membership.user_details.avatar_url} alt="" />
          : <div className="gdp__jr-avatar-placeholder">
              {(membership.user_details?.full_name || membership.user_details?.email || '?').charAt(0).toUpperCase()}
            </div>
        }
      </div>
      <div className="gdp__jr-info">
        <span className="gdp__jr-name">
          {membership.user_details?.full_name || membership.user_details?.email}
        </span>
        <span className="gdp__jr-email">{membership.user_details?.email}</span>
      </div>
      <div className="gdp__jr-actions">
        <button
          className="btn btn--primary btn--sm"
          onClick={() => onApprove(userId)}
          disabled={approving || declining}
        >
          {approving ? <RefreshCw size={13} className="spinning" /> : <Check size={13} />}
          Approve
        </button>
        <button
          className="btn btn--ghost btn--sm btn--danger"
          onClick={() => onDecline(userId)}
          disabled={approving || declining}
        >
          {declining ? <RefreshCw size={13} className="spinning" /> : <X size={13} />}
          Decline
        </button>
      </div>
    </div>
  );
};

export default GroupDetailPage;
